<template>
    <portal>
        <div class=" d-flex align-center" >
            <v-icon color="primary" size="30">mdi-calculator</v-icon>
            <h1 style="margin-bottom: 0px;" class="headline ml-4 font-weight-bold grey--text--darken-2 text-uppercase">My Plan</h1>
        </div>
        <v-row class="income-planner-row-one">
            <v-col class="col-md-3 col-sm-6">
               <div
                   v-if="individuals[0]"
                   class="income-planner-individual">
                   <div style="display: inline-block;"><v-icon x-large>mdi-account</v-icon></div>
                   <div style="vertical-align: top">
                       <h2>{{ individuals[0].name }}</h2>
                       <p>{{ individuals[0].current_age }} years old</p>
                       <p>Retired at {{ individuals[0].retirement_age }}</p>
                   </div>
               </div>
            </v-col>
            <v-col class="col-md-3 col-sm-6">
                <div
                    v-if="individuals[1]"
                    class="income-planner-individual">
                    <div style="display: inline-block;"><v-icon x-large>mdi-account</v-icon></div>
                    <div style="vertical-align: top">
                        <h2>{{ individuals[1].name }}</h2>
                        <p>{{ individuals[1].current_age }} years old</p>
                        <p>Retired at {{ individuals[1].retirement_age }}</p>
                    </div>
                </div>
            </v-col>
            <v-col
                class="col-md-3 col-sm-6 income-planner-variables-highlight"
                style="text-align: left">
                        <h2>{{ inflation }}%</h2>
                        <p>
                            Assumed Inflation Rate
                            <v-tooltip
                                right>
                                <template v-slot:activator="{ on, attrs }">
                                    <div
                                        v-bind="attrs"
                                        v-on="on"
                                        class="list-tooltip"
                                        style="vertical-align: text-bottom; display: inline">
                                        <v-icon small>mdi-help-rhombus</v-icon>
                                    </div>
                                </template>
                                <span>{{  tooltipText('Inflation rate') }}</span>
                            </v-tooltip>
                        </p>
            </v-col>
            <v-col class="col-md-3 col-sm-6 income-planner-variables-highlight">
                <h2>{{ incomeNeeds.monthlyIncomeNeeds | money }}</h2>
                <p>
                    Anticipated Gross Income In Retirement/Mth
                    <v-tooltip
                        style="vertical-align: text-bottom;"
                        right>
                        <template v-slot:activator="{ on, attrs }">
                            <div
                                v-bind="attrs"
                                v-on="on"
                                class="list-tooltip"
                                style="vertical-align: text-bottom; display: inline">
                                <v-icon small>mdi-help-rhombus</v-icon>
                            </div>
                        </template>
                        <span>{{  tooltipText('Adjustable Income - Monthly income needs') }}</span>
                    </v-tooltip>
                </p>
            </v-col>
        </v-row>
        <v-row>
            <v-col class="col-sm-12 d-flex">
                    <div  style="width: 25%; padding: 0px;">
                        <Meter
                            v-if="riskMeter.isEnabled"
                            title="Risk meter"
                            :arrow-degree="riskMeter.degree"
                            :value="riskMeter.number"
                            :background-asset-url="this.$api_url + '/images/risk-meter.png'"
                            :arrow-asset-url="this.$api_url + '/images/arrow-meters.png'"/>
                    </div>
                    <div style="width: 25%;">
                        <Meter
                            v-if="successMeter.isEnabled"
                            title="Probability of success"
                            :arrow-degree="successMeter.degree"
                            :value="successMeter.percentage"
                            :background-asset-url="this.$api_url + '/images/success-meter.png'"
                            :arrow-asset-url="this.$api_url + '/images/arrow-meters.png'"/>
                    </div>
                    <v-col class="col-md-2" style="padding: 0px;"></v-col>
            </v-col>
        </v-row>
        <v-row class="mb-10">
            <v-col class="col-sm-12">
                <v-dialog
                    overlay-color="white"
                    v-model="dialog"
                    width="800px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            color="primary"
                            dark
                            v-bind="attrs"
                            v-on="on"
                            class="adjust-my-plan-floating">
                            Adjust My Plan
                        </v-btn>
                    </template>
                    <v-card>
                        <v-expansion-panel>
                            <v-expansion-panel-header
                                :hide-actions="true"
                                class="primary title white--text text-h5"
                                color="white">Adjust My Plan - What If</v-expansion-panel-header>
                            <v-expansion-panels
                                :accordion="true"
                                style="padding-left: 10px; padding-right: 10px">
                                <income-needs-panel
                                    :monthly-income-needs="incomeNeeds.monthlyIncomeNeeds"
                                    :adjustments="incomeNeeds.adjustments"
                                    :relative-plan-year-dropdown="incomeNeeds.relativePlanYears"
                                    :disabled="incomeNeedsPanelDisabled"
                                    :household-id="this.user.household_id"
                                    @adjustmentUpdated="submitIncomeNeedsAdjustmentUpdate"
                                    @incomeNeedsUpdated="submitIncomeNeedsUpdate"
                                    class="mt-5"/>
                                <retirement-panel
                                    :individuals="individuals"
                                    :disabled="retirementPanelDisabled"
                                    @retirementAgeUpdated="submitRetirementAgeUpdate"/>
                                <annual-savings
                                    :disabled="updating"
                                    :savings-account="savingsAccount"
                                    :relative-plan-year-dropdown="incomeNeeds.relativePlanYears"
                                    @annualSavingsUpdated="submitAnnualSavingsUpdate"/>
                            </v-expansion-panels>
                            <div style="text-align: center">
                                <v-btn
                                    depressed
                                    rounded
                                    class="primary mt-4 mb-4 text--white"
                                    @click="resetIncomePlanner">Reset to default</v-btn>
                            </div>
                        </v-expansion-panel>
                    </v-card>
                </v-dialog>

            </v-col>
            <v-col class="col-sm-12">
                <v-card v-if="!chartsInitialLoading">
                    <income-chart
                        :relative-year-monthly-needs="sipsTable.relativeYearMonthlyNeeds"
                        :relative-year-monthly-income="sipsTable.relativeYearMonthlyIncome"
                        class="mb-10"/>
                    <portfolio-chart
                        :relative-year-portfolio-starting-balance="sipsTable.relativeYearPortfolioStartingBalance"
                        :relative-year-benchmark-starting-balance="sipsTable.relativeYearBenchmarkStartingBalance"
                        :show-bench-mark="showBenchMark"/>
                </v-card>
            </v-col>
        </v-row>


        <sips-table
            :headers="sipsTable.sipsTable.headers"
            :columns="sipsTable.sipsTable.columns"
            :years-from-passing-away="householdPassingAwayYears"
            :money-format-column="sipsTable.sipsTable.moneyFormat"/>
        <template v-slot:disclosure>
            <v-col class="px-12 py-8" v-html="company.disclosure_client_portal_see_my_plan"></v-col>
        </template>
    </portal>
</template>

<style>
    .income-planner-row-one h2 {
        font-size: 2.5rem;
        line-height: 2.5rem;
        color: #1d5be5;
    }

    .income-planner-individual p {
        margin-bottom: 0px;
    }

    .income-planner-individual div {
        display: inline-block;
    }

    #adjust-my-plan-header .v-icon{
        color: white;
    }

    .adjust-my-plan-floating {
        position: fixed;
        z-index: 99999;
        bottom: 30px;
        right: 100px;
    }
</style>

<script>

import Portal from "@/components/Portal";
import IncomeChart from "@/components/income-planner/IncomeChart";
import PortfolioChart from "@/components/income-planner/PortfolioChart";
import SipsTable from "@/components/income-planner/SipsTable";
import IncomeNeedsPanel from "@/components/income-planner/adjust-your-plan/IncomeNeedsPanel";
import RetirementPanel from "@/components/income-planner/adjust-your-plan/RetirementPanel";
import AnnualSavings from "@/components/income-planner/adjust-your-plan/AnnualSavings";
import Meter from "@/components/income-planner/Meter";

import {mapGetters, mapState, mapActions} from "vuex";
import {individuals} from "@/store/modules/individuals";

export default {
    name: 'IncomePlanner',

    components: {
        IncomeChart,
        PortfolioChart,
        Portal,
        SipsTable,
        IncomeNeedsPanel,
        RetirementPanel,
        AnnualSavings,
        Meter,
    },

    computed: {
        ...mapGetters('auth', [
            'user',
        ]),

        ...mapState('incomePlanner', [
            'sipsTable',
            'riskMeter',
            'successMeter',
            'yearsPeopleDie',
            'showBenchMark',
            'incomeNeeds',
            'inflation'
        ]),

        ...mapState('company', [
            'company',
        ]),

        ...mapState('individuals', [
            'individuals',
        ]),

        ...mapState('accounts', [
            'savingsAccount',
        ]),
        personeOne() {
            return individuals[0];
        },
        householdPassingAwayYears() {
            return {
                personOne: this.yearsPeopleDie[0],
                personTwo: this.yearsPeopleDie[1]
            };
        }
    },

    inject: ['tooltipText'],

    data() {
        return {
            dialog: false,
            updating: false,
            incomeNeedsPanelDisabled: false,
            retirementPanelDisabled: false,
            chartsInitialLoading: true,
        }
    },

    created() {
        this.$store.dispatch('incomePlanner/fetchIncomePlanner', this.user.household_id).then( () => {
            this.chartsInitialLoading = false;
        });
        this.fetchIncomePlannerVariables(this.user.household_id);
        this.loadSavingsAccount();

    },

    methods: {
        ...mapActions('incomePlanner', [
            'fetchIncomePlanner',
            'updateIncomeNeeds',
            'fetchIncomePlannerVariables',
            'createIncomeNeedsAdjustment',
            'updateIncomeNeedsAdjustment',
            'deleteIncomeNeedsAdjustment'
        ]),

        ...mapActions('household', [
            'resetVariables'
        ]),

        ...mapActions('individuals', [
            'saveIndividual'
        ]),

        ...mapActions('accounts', [
            'loadSavingsAccount',
            'updateSavingsAccount'
        ]),

        submitIncomeNeedsUpdate({payload}) {
            this.incomeNeedsPanelDisabled = true;
            this.updateIncomeNeeds({payload, householdId: this.user.household_id})
            .then(
                () => this.incomeNeedsPanelDisabled = false,
                (errors) => {
                    for (let field in errors) {

                        if (errors['monthlyIncomeNeeds']) {
                            this.$noty.error(errors['monthlyIncomeNeeds']);
                        }

                        delete errors['monthlyIncomeNeeds'];

                        errors[field].forEach((error) => {
                            //Find the adjustment that is associated with each error.
                            let fields = field.split('.');
                            let adjustmentIndex = fields[2];
                            if (adjustmentIndex) {
                                let adjustmentName = this.incomeNeeds.adjustments[adjustmentIndex].description;
                                this.$noty.error(`For ${adjustmentName}: ` + error);
                            }
                        });
                    }

                    this.incomeNeedsPanelDisabled = false;
                }
            );
        },

        submitIncomeNeedsAdjustmentUpdate({ payload, action}) {
            let response = null;
            if (action === 'add' || action === 'add-default') {
                this.incomeNeedsPanelDisabled = true;
                response = this.createIncomeNeedsAdjustment(
                    {
                        payload,
                        addDefault: action === 'add-default',
                        householdId: this.user.household_id
                    }
                )
            } else if (action === 'update') {
                this.incomeNeedsPanelDisabled = true;
                response = this.updateIncomeNeedsAdjustment(
                    {
                        payload,
                        householdId: this.user.household_id
                    }
                );
            } else if (action === 'delete') {
                 if (window.confirm('Please confirm again to delete.')) {
                     this.incomeNeedsPanelDisabled = true;
                     response = this.deleteIncomeNeedsAdjustment(
                         {
                             payload,
                             householdId: this.user.household_id,
                         }
                     )
                 }
            }

            Promise.resolve(response).then(
                () => {
                    this.incomeNeedsPanelDisabled = false;
                },
                (errors) => {
                    this.incomeNeedsPanelDisabled = false
                    for (let field in errors) {
                        let error = '';
                        errors[field].forEach((errorMsg) => {
                            error += errorMsg;
                        });
                        this.$noty.error(error);
                    }
                }
            )
        },

        submitRetirementAgeUpdate({individualIndex, payload}) {
            this.retirementPanelDisabled = true;
            this.saveIndividual({individualIndex, payload})
                .then(() => {
                    return this.fetchIncomePlanner(this.user.household_id);
                }).then(() => {
                    this.retirementPanelDisabled = false;
                })
        },

        submitAnnualSavingsUpdate({ payload }) {
            this.updating = true;
            this.updateSavingsAccount({ payload })
                .then(() => {
                    return this.fetchIncomePlanner(this.user.household_id);
                }).then(() => {
                    this.updating = false;
                })
        },

        resetIncomePlanner() {
            if (window.confirm('Please confirm again to reset the income planner')) {
                this.resetVariables(this.user.household_id).then(
                    () => {
                        this.fetchIncomePlannerVariables(this.user.household_id);
                        this.$store.dispatch('household/fetchHousehold', this.user.household_id);
                        this.$store.dispatch('incomePlanner/fetchIncomePlanner', this.user.household_id);
                        this.$store.dispatch('individuals/loadIndividuals');
                        this.$store.dispatch('accounts/loadSavingsAccount')
                        this.$store.dispatch('individuals/loadIndividualsDropdown');
                        this.$store.dispatch('company/loadCompany');
                        this.$store.dispatch('beneficiaries/loadBeneficiaries');
                        this.$store.dispatch('constants/loadConstants');
                        this.$store.dispatch('advisor/loadAdvisor');
                    },
                    () => {
                        this.$noty.error('The reset has failed');
                    }
                );
            }
        }
    }
}
</script>
